import { Button } from "@bcc/components/ui/button";
import { IndeterminateCheckbox } from "@bcc/components/ui/checkbox";
import { cn } from "@bcc/lib/utils";
import type { DataTableRow, DataTableRowTone } from "@client-shared";
import { flexRender, type Row, type RowSelectionState } from "@tanstack/react-table";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import * as React from "react";
import {
  getSelectionButtonToneClass,
  setExpanderButtonTestId,
  setGroupSelectButtonTestId,
  setRowCheckboxTestId,
  setRowTestId,
} from "./data-table.utils";

import type { GroupedRowActionHelpers } from "./data-table.types";

interface DataTableRowComponentProps<T extends object> {
  row: Row<DataTableRow<T>>;
  selectable?: boolean;
  onClick?: (row: Row<DataTableRow<T>>) => void;
  showExpanderColumn?: boolean;
  showGroupCount?: boolean;
  renderGroupSummary?: (rows: DataTableRow<T>[]) => React.ReactNode;
  rowSelection?: RowSelectionState;
  groupedRowActions?: (row: Row<DataTableRow<T>>, helpers: GroupedRowActionHelpers<T>) => React.ReactNode;
  groupSelectLabel?: (groupRow: Row<DataTableRow<T>>) => string;
  helpers?: GroupedRowActionHelpers<T>;
}

/**
 * Returns true when every non-disabled leaf row in the group is selected.
 * Uses rowSelection directly because getIsAllSubRowsSelected() includes
 * disabled rows (which can never be selected), so it always returns false.
 */
function getIsGroupChecked<T extends object>(groupRow: Row<DataTableRow<T>>, rowSelection?: RowSelectionState): boolean {
  const selectableLeaves = groupRow.getLeafRows().filter((r) => !r.original.isDisabled);
  if (selectableLeaves.length === 0) return false;
  const selection = (rowSelection ?? {}) as Record<string, boolean>;
  return selectableLeaves.every((r) => selection[r.id] === true);
}

/**
 * Custom indeterminate check for grouped rows.
 * Excludes disabled rows from the calculation — matching client DataTable.
 */
function getIsGroupIndeterminate<T extends object>(groupRow: Row<DataTableRow<T>>, rowSelection?: RowSelectionState): boolean {
  if (!groupRow.getIsGrouped() || !groupRow.subRows || !rowSelection) {
    return groupRow.getIsSomeSelected();
  }
  const selection = rowSelection as Record<string, boolean>;
  let hasSelected = false;
  let hasUnselected = false;

  const checkGroup = (r: Row<DataTableRow<T>>): void => {
    if (r.getIsGrouped() && r.subRows) {
      for (const subRow of r.subRows) {
        checkGroup(subRow);
      }
      return;
    }
    if (r.original.isDisabled) return;
    if (selection[r.id] === true) {
      hasSelected = true;
    } else {
      hasUnselected = true;
    }
  };

  checkGroup(groupRow);
  return hasSelected && hasUnselected;
}

/**
 * Returns true if every selectable leaf row in the group is disabled.
 */
function isGroupAllDisabled<T extends object>(groupRow: Row<DataTableRow<T>>): boolean {
  return groupRow.getLeafRows().every((r) => r.original.isDisabled);
}

/**
 * Returns the Tailwind class for a row tone.
 * Uses explicit class strings so Tailwind can detect them at build time.
 */
function getToneClass(tone?: DataTableRowTone): string {
  switch (tone) {
    case "success":
      return "bg-green-500/20 dark:bg-green-500/20";
    case "warning":
      return "bg-amber-500/20 dark:bg-amber-500/20";
    case "critical":
      return "bg-red-500/20 dark:bg-red-500/20";
    case "subdued":
      return "bg-muted/50 text-muted-foreground";
    case "canceled":
      return "bg-muted/50 text-muted-foreground line-through";
    default:
      return "";
  }
}

interface GroupSelectButtonProps {
  rowId: string;
  label: string;
  isChecked: boolean;
  isDisabled: boolean;
  onToggle: (checked: boolean) => void;
}

function GroupSelectButton({ rowId, label, isChecked, isDisabled, onToggle }: Readonly<GroupSelectButtonProps>) {
  return (
    <Button
      type="button"
      testId={setGroupSelectButtonTestId(rowId)}
      variant="outline"
      size="sm"
      className={cn("text-xs font-normal", getSelectionButtonToneClass(isChecked))}
      aria-pressed={isChecked}
      disabled={isDisabled}
      onClick={(e) => {
        e.stopPropagation();
        onToggle(!isChecked);
      }}
    >
      {label}
    </Button>
  );
}

function getRowClassName(options: {
  isSelected: boolean;
  tone?: DataTableRowTone;
  hasError?: boolean;
  isClickable: boolean;
  isGrouped: boolean;
}): string {
  return cn(
    "border-b border-border transition-colors",
    "hover:bg-muted/30",
    options.isSelected && "bg-muted/50",
    getToneClass(options.tone),
    options.hasError && "bg-red-500/5",
    options.isClickable && "cursor-pointer",
    options.isGrouped && "bg-muted/20 font-medium"
  );
}

const CLICKABLE_ROW_ROLE = "button";

// Marks regions whose clicks/keys belong to their own controls and must not trigger the row's handlers.
const ROW_ACTIONS_ATTRIBUTE = "data-row-actions";

const isFromRowActions = (e: React.SyntheticEvent): boolean =>
  e.target instanceof Element && e.target.closest(`[${ROW_ACTIONS_ATTRIBUTE}]`) !== null;

interface RowExpanderCellProps {
  rowId: string;
  canExpand: boolean;
  isExpanded: boolean;
  onExpandClick: (e: React.MouseEvent) => void;
}

function RowExpanderCell({ rowId, canExpand, isExpanded, onExpandClick }: Readonly<RowExpanderCellProps>) {
  return (
    <td className="w-10 px-2 py-4">
      {canExpand ? (
        <button
          type="button"
          data-testid={setExpanderButtonTestId(rowId)}
          onClick={onExpandClick}
          className="p-1 hover:bg-muted rounded transition-colors"
          aria-label={isExpanded ? "Collapse row" : "Expand row"}
        >
          {isExpanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </button>
      ) : (
        <span className="w-4 h-4 inline-block" />
      )}
    </td>
  );
}

interface RowSelectionCellProps<T extends object> {
  row: Row<DataTableRow<T>>;
  rowSelection?: RowSelectionState;
  isGroupDisabled: boolean;
  onCheckboxChange: (checked: boolean) => void;
}

function RowSelectionCell<T extends object>({ row, rowSelection, isGroupDisabled, onCheckboxChange }: Readonly<RowSelectionCellProps<T>>) {
  const isGrouped = row.getIsGrouped();
  const isDisabled = row.original.isDisabled;

  return (
    <td className="w-[50px] px-4 py-4" onClick={(e) => e.stopPropagation()}>
      {row.original.isLoading && !isGrouped ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : (
        <IndeterminateCheckbox
          testId={setRowCheckboxTestId(row.id)}
          checked={isGrouped ? getIsGroupChecked(row, rowSelection) : row.getIsSelected()}
          indeterminate={isGrouped ? isGroupDisabled || getIsGroupIndeterminate(row, rowSelection) : isDisabled || row.getIsSomeSelected()}
          disabled={isGrouped ? isGroupDisabled : !row.getCanSelect() || isDisabled}
          onChange={onCheckboxChange}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          aria-label={`Select row ${row.id}`}
        />
      )}
    </td>
  );
}

interface GroupedRowCellProps<T extends object> {
  row: Row<DataTableRow<T>>;
  isGroupDisabled: boolean;
  showGroupCount: boolean;
  renderGroupSummary?: (rows: DataTableRow<T>[]) => React.ReactNode;
  groupedRowActions?: (row: Row<DataTableRow<T>>, helpers: GroupedRowActionHelpers<T>) => React.ReactNode;
  helpers?: GroupedRowActionHelpers<T>;
  groupSelectLabel?: string;
  isGroupChecked: boolean;
  onGroupToggle: (checked: boolean) => void;
}

function GroupedRowCell<T extends object>({
  row,
  isGroupDisabled,
  showGroupCount,
  renderGroupSummary,
  groupedRowActions,
  helpers,
  groupSelectLabel,
  isGroupChecked,
  onGroupToggle,
}: Readonly<GroupedRowCellProps<T>>) {
  const hasGroupSelectButton = groupSelectLabel !== undefined;

  return (
    <td
      colSpan={row.getVisibleCells().length + (hasGroupSelectButton ? 1 : 0)}
      className={cn("px-4 py-3 text-sm", isGroupDisabled && "text-muted-foreground")}
    >
      <span className="inline-flex w-full items-center gap-2">
        {hasGroupSelectButton && (
          <GroupSelectButton
            rowId={row.id}
            label={groupSelectLabel}
            isChecked={isGroupChecked}
            isDisabled={isGroupDisabled}
            onToggle={onGroupToggle}
          />
        )}
        {row
          .getVisibleCells()
          .filter((cell) => cell.getIsGrouped())
          .map((cell) => (
            <React.Fragment key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</React.Fragment>
          ))}
        {showGroupCount && (
          <span
            className={cn(
              "inline-flex items-center justify-center rounded-full text-xs font-semibold px-2.5 py-0.5",
              isGroupDisabled ? "bg-muted-foreground/10 text-muted-foreground" : "bg-primary/10 text-primary"
            )}
          >
            {row.subRows.length}
          </span>
        )}
        {renderGroupSummary && <span className="ml-1">{renderGroupSummary(row.subRows.map((r) => r.original))}</span>}
        {groupedRowActions && helpers && (
          <span className="ml-auto shrink-0" {...{ [ROW_ACTIONS_ATTRIBUTE]: "" }}>
            {groupedRowActions(row, helpers)}
          </span>
        )}
      </span>
    </td>
  );
}

/**
 * DataTableRowComponent
 *
 * Renders a single table row with optional selection checkbox,
 * row tones, click handling, and grouped row support.
 */
export function DataTableRowComponent<T extends object>({
  row,
  selectable = false,
  onClick,
  showExpanderColumn = false,
  showGroupCount = false,
  renderGroupSummary,
  rowSelection,
  groupedRowActions,
  groupSelectLabel,
  helpers,
}: Readonly<DataTableRowComponentProps<T>>) {
  const original = row.original;
  const tone = original.tone;
  const isDisabled = original.isDisabled;
  const hasError = original.hasError;
  const isGrouped = row.getIsGrouped();
  const canExpand = row.getCanExpand();
  const isExpanded = row.getIsExpanded();
  const depth = row.depth;
  const isGroupDisabled = isGrouped && isGroupAllDisabled(row);
  const handleClick = React.useCallback(
    (e: React.MouseEvent) => {
      if (onClick && !isDisabled && !isFromRowActions(e)) {
        onClick(row);
      }
    },
    [onClick, row, isDisabled]
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === "Enter" || e.key === " ") && onClick && !isDisabled && !isFromRowActions(e)) {
        e.preventDefault();
        onClick(row);
      }
    },
    [onClick, row, isDisabled]
  );

  const handleExpandClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      row.toggleExpanded();
    },
    [row]
  );

  const handleCheckboxChange = React.useCallback(
    (checked: boolean) => {
      if (isGrouped) {
        row.getLeafRows().forEach((leafRow) => {
          if (leafRow.getCanSelect()) {
            leafRow.toggleSelected(checked);
          }
        });
      } else {
        row.toggleSelected(checked);
      }
    },
    [isGrouped, row]
  );

  const hasGroupSelectButton = selectable && isGrouped && Boolean(groupSelectLabel);

  const isClickable = Boolean(onClick) && !isDisabled;

  return (
    <tr
      data-testid={setRowTestId(row.id)}
      className={getRowClassName({ isSelected: row.getIsSelected(), tone, hasError, isClickable, isGrouped })}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={isClickable ? 0 : undefined}
      role={onClick ? CLICKABLE_ROW_ROLE : undefined}
      aria-disabled={isDisabled}
      aria-expanded={canExpand ? isExpanded : undefined}
      data-state={row.getIsSelected() ? "selected" : undefined}
      data-depth={depth}
    >
      {/* Expander column for grouped rows */}
      {showExpanderColumn && (
        <RowExpanderCell rowId={row.id} canExpand={canExpand} isExpanded={isExpanded} onExpandClick={handleExpandClick} />
      )}
      {selectable && !hasGroupSelectButton && (
        <RowSelectionCell row={row} rowSelection={rowSelection} isGroupDisabled={isGroupDisabled} onCheckboxChange={handleCheckboxChange} />
      )}
      {isGrouped ? (
        <GroupedRowCell
          row={row}
          isGroupDisabled={isGroupDisabled}
          showGroupCount={showGroupCount}
          renderGroupSummary={renderGroupSummary}
          groupedRowActions={groupedRowActions}
          helpers={helpers}
          groupSelectLabel={hasGroupSelectButton && groupSelectLabel ? groupSelectLabel(row) : undefined}
          isGroupChecked={getIsGroupChecked(row, rowSelection)}
          onGroupToggle={handleCheckboxChange}
        />
      ) : (
        row.getVisibleCells().map((cell) => (
          <td key={cell.id} className="px-4 py-4 text-sm">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))
      )}
    </tr>
  );
}
